import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Clock, MapPin, Phone, Scissors } from 'lucide-react'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

const salons = [
  {
    name: 'Nashi Salon Centro',
    image: '/images/stores/milano-dante.jpg',
    address: 'Via Dante, 14 — Milano',
    phone: '+39 02 8645 2310',
    hours: 'Lun–Sab 10:00 – 19:00',
    href: '#prenota-centro',
  },
  {
    name: 'Nashi Salon Corso Vercelli',
    image: '/images/stores/milano-merlata.jpg',
    address: 'Corso Vercelli, 34 — Milano',
    phone: '+39 02 4819 5623',
    hours: 'Lun–Sab 10:00 – 19:00',
    href: '#prenota-vercelli',
  },
  {
    name: 'Nashi Salon Porta Genova',
    image: '/images/stores/como.jpg',
    address: 'Via Vigevano, 8 — Milano',
    phone: '+39 02 8374 1102',
    hours: 'Lun–Sab 10:00 – 19:00',
    href: '#prenota-genova',
  },
  {
    name: 'Nashi Salon Porta Venezia',
    image: '/images/stores/bergamo.jpg',
    address: 'Corso Buenos Aires, 76 — Milano',
    phone: '+39 02 2049 3341',
    hours: 'Lun–Sab 10:00 – 19:00',
    href: '#prenota-venezia',
  },
]

const services = [
  {
    title: 'Taglio',
    description:
      'Dalla consulenza iniziale alla piega finale, i nostri hairstylist studiano il taglio perfetto per esaltare i tratti del viso e la personalità di ogni cliente.',
    price: 'da 45€',
  },
  {
    title: 'Trattamento Keratin',
    description:
      'Ricostruzione profonda con cheratina per capelli secchi, fragili e trattati. Risultato immediato: morbidezza, lucentezza e movimento naturale.',
    price: 'da 85€',
  },
  {
    title: 'Laminazione',
    description:
      'Il segreto per una chioma lucente e setosa come mai prima. La laminazione sigilla le squame e dona un effetto specchio che dura nel tempo.',
    price: 'da 65€',
  },
  {
    title: 'Trattamento Filler & Ricostruzione',
    description:
      'Azione riempitiva e ristrutturante per capelli danneggiati. Ripara le punte e previene la rottura, restituendo corpo e forza alla fibra capillare.',
    price: 'da 75€',
  },
]

export function SaloniPage() {
  const { t } = useTranslation('common')

  return (
    <main>
      <InnerHero
        eyebrow="Store & Salon"
        title="Benvenuti nel"
        titleItalic="Nashi Salon."
        body="Luoghi esclusivi dove la cura dei capelli diventa un momento di puro benessere. Vivi l'atmosfera Nashi Argan e lascia che i nostri esperti prendano cura di te."
        image="/images/nashi-salon-experience.jpg"
        imageAlt="Interno Nashi Salon"
        breadcrumbs={[
          { label: 'Home', href: '/it' },
          { label: t('nav.nashiSalon', 'Nashi Salon') },
        ]}
      />

      {/* ───────── Philosophy ───────── */}
      <section className="relative bg-cream py-24 md:py-36 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-10 right-[-2%] select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(12rem, 26vw, 26rem)', lineHeight: 0.9 }}
        >
          n
        </div>

        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="lg:col-span-5"
            >
              <span className="nashi-eyebrow">L'esperienza</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">Trasforma l'attesa</span>
                <span className="block italic font-light text-argan-deep">
                  in piacere.
                </span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="lg:col-span-7 space-y-6 text-bruno-soft text-base md:text-lg leading-relaxed"
            >
              <p>
                Nashi Salon si offre come luogo esclusivo dove ogni trattamento
                è un'esperienza sensoriale. Dalla consulenza personalizzata alla
                piega finale, il nostro team ti guida nella scelta dei prodotti
                più adatti alle tue esigenze.
              </p>
              <p>
                Ogni salone è pensato per regalare un momento di bellezza
                completa: profumazione iconica, atmosfera rilassante e
                professionisti che mettono la tua soddisfazione al primo posto.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── Salons Grid ───────── */}
      <section className="relative bg-cream-soft py-20 md:py-28 overflow-hidden border-y border-bruno/10">
        <div className="nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl mb-16 md:mb-20"
          >
            <span className="nashi-eyebrow">Prenota il tuo appuntamento</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">I nostri</span>
              <span className="block italic font-light text-argan-deep">
                saloni a Milano.
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {salons.map((salon, i) => (
              <motion.article
                key={salon.name}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.95, delay: (i % 2) * 0.1 }}
                className="group bg-cream border border-bruno/10 overflow-hidden hover:shadow-lg transition-shadow duration-700"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={salon.image}
                    alt={salon.name}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h3
                    className="font-display text-bruno"
                    style={{
                      fontSize: 'clamp(1.375rem, 2vw, 1.75rem)',
                      lineHeight: 1.1,
                    }}
                  >
                    {salon.name}
                  </h3>

                  <div className="mt-5 space-y-3 text-sm text-bruno-soft">
                    <div className="flex items-start gap-3">
                      <MapPin
                        className="h-4 w-4 mt-0.5 text-argan-deep shrink-0"
                        strokeWidth={1.5}
                      />
                      <span>{salon.address}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock
                        className="h-4 w-4 mt-0.5 text-argan-deep shrink-0"
                        strokeWidth={1.5}
                      />
                      <span>{salon.hours}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone
                        className="h-4 w-4 mt-0.5 text-argan-deep shrink-0"
                        strokeWidth={1.5}
                      />
                      <a
                        href={`tel:${salon.phone.replace(/\s/g, '')}`}
                        className="hover:text-bruno transition-colors"
                      >
                        {salon.phone}
                      </a>
                    </div>
                  </div>

                  <a
                    href={salon.href}
                    className="mt-8 inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-8 py-4 hover:bg-argan-deep transition-colors group/btn"
                  >
                    Prenota ora
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform duration-500 group-hover/btn:translate-x-1"
                      strokeWidth={1.75}
                    />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Services ───────── */}
      <section className="relative bg-cream py-24 md:py-36 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-6%] left-[-4%] select-none font-display italic text-argan/[0.05]"
          style={{ fontSize: 'clamp(12rem, 26vw, 26rem)', lineHeight: 0.85 }}
        >
          s
        </div>

        <div className="relative nashi-container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl mb-16 md:mb-20"
          >
            <span className="nashi-eyebrow">Servizi</span>
            <h2
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">
                Affidati ai nostri esperti per rinnovare
              </span>
              <span className="block italic font-light text-argan-deep">
                il tuo look ed esaltare la tua naturale bellezza.
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.9, delay: i * 0.08 }}
                className="group bg-cream-soft border border-bruno/10 p-8 md:p-10 hover:bg-cream hover:border-argan/30 transition-all duration-700"
              >
                <div className="flex items-center justify-between mb-6">
                  <Scissors
                    className="h-6 w-6 text-argan-deep"
                    strokeWidth={1.25}
                  />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-bruno-muted">
                    {service.price}
                  </span>
                </div>
                <h3
                  className="font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.25rem, 1.8vw, 1.5rem)',
                    lineHeight: 1.1,
                  }}
                >
                  {service.title}
                </h3>
                <div
                  aria-hidden="true"
                  className="h-px w-10 bg-argan/40 mt-5 mb-5 group-hover:w-16 transition-all duration-700"
                />
                <p className="text-bruno-soft text-[15px] leading-[1.75]">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Careers ───────── */}
      <section className="relative bg-bruno text-cream overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1 }}
            className="relative aspect-[4/3] lg:aspect-auto overflow-hidden"
          >
            <img
              src="/images/store-interior.jpg"
              alt="Team Nashi Salon"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-bruno/40 to-transparent lg:hidden" />
          </motion.div>

          <div className="flex flex-col justify-center px-8 py-16 md:px-14 md:py-24 lg:px-20 lg:py-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
            >
              <span className="inline-flex items-center gap-3 text-[0.6875rem] font-medium uppercase tracking-[0.28em] text-argan-soft">
                <span className="block w-6 h-px bg-argan-soft" />
                Carriere
              </span>
              <h2
                className="mt-6 font-display text-cream"
                style={{
                  fontSize: 'clamp(2rem, 3.6vw, 3rem)',
                  lineHeight: 1.02,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">Lavora</span>
                <span className="block italic font-light text-argan-soft">
                  con noi.
                </span>
              </h2>
              <p className="mt-6 text-cream/80 leading-relaxed max-w-md">
                Inviaci la tua candidatura ed entra nel team Nashi Argan.
                Siamo sempre alla ricerca di talenti appassionati di bellezza
                e cura dei capelli.
              </p>
              <LocalizedLink
                to="contacts"
                className="mt-10 inline-flex items-center gap-3 bg-cream text-bruno font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-soft transition-colors group"
              >
                Candidati
                <ArrowRight
                  className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </LocalizedLink>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── Store Locator CTA ───────── */}
      <section className="bg-cream-soft py-20 md:py-28">
        <div className="nashi-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">Trova il tuo salone</span>
            <h2
              className="mt-6 font-display text-bruno mx-auto max-w-2xl"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">Scopri il Nashi Salon</span>
              <span className="block italic font-light text-argan-deep">
                più vicino a te.
              </span>
            </h2>
            <p className="mt-6 text-bruno-soft leading-relaxed max-w-lg mx-auto">
              Una rete di Saloni Nashi Argan in tutta Italia, dove vivere
              l'esperienza completa della cura dei capelli.
            </p>
            <LocalizedLink
              to="storeLocator"
              className="mt-10 inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors group"
            >
              Store Locator
              <ArrowRight
                className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1"
                strokeWidth={1.75}
              />
            </LocalizedLink>
          </motion.div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
